import _ from 'lodash';

import { teamFeaturesConstants } from '../../constants/appConstants';

import type { GetTeamsResponse } from '../../queries/teamQuery';
import type { ZustandStore } from '../store.type';
import type { User } from './userSlice';
import type { NamedSet } from 'zustand/middleware';

export type OptionalTeamProperties = {
	ENABLE_MULTIUSE?: 'true' | 'false';
	ENABLE_SINGLEUSE?: 'true' | 'false';
	ENABLE_SUBSCRIPTIONS?: 'true' | 'false';
	PAUSE_VIRTUAL_CARD_CREATION?: 'true' | 'false';
	REQUEST_LIMIT?: string;
};

type Team = {
	id: string | undefined;
	selected: boolean;
	useRole: string;
	name: string;
	createdDate: string;
	createdBy: { fullName: string };
	numberOfCards: number;
	numberOfMembers: number;
	state: any;
	properties: OptionalTeamProperties;
	owners: any[];
	sourceCards: any[];
	isTeamNameError: boolean;
	isTeamRequestLimitError: boolean;
	users?: User[];
};

type TeamState = {
	team: {
		teamName: string;
		initialTeamName: string;
		teamId: string;
		teams?: GetTeamsResponse[];
		state: string;
		teamRequestLimit: string;
		team: null | Partial<Team> | GetTeamsResponse;
		features?: Record<string, unknown>;
		properties?: Record<string, unknown>;
		decoratedUser: Record<string, unknown>;
		teamProperty: string;
		owners: User[];
		hasChanges: boolean; // checks if there are changes in team details
		isTeamNameError: boolean; // used to check duplicate team name
		isTeamRequestLimitError: boolean; // used to invalid approval limit
	};
};

type TeamActions = {
	setTeams: (teams: TeamState['team']['teams']) => void;
	setTeamOwners: (owners: TeamState['team']['owners']) => void;
	setTeam: (team: TeamState['team']['team']) => void;
	setTeamId: (teamId: TeamState['team']['teamId']) => void;
	setTeamName: (teamName: TeamState['team']['teamName']) => void;
	setInitialTeamName: (teamName: TeamState['team']['initialTeamName']) => void;
	setIsTeamNameError: (isTeamNameError: TeamState['team']['isTeamNameError']) => void;
	setTeamProperty: (properties: Record<string, unknown>) => void;
	setDecoratedUser: (decoratedUser: TeamState['team']['decoratedUser']) => void;
	setTeamRequestLimit: (teamRequestLimit: TeamState['team']['teamRequestLimit']) => void;
	setHasChanges: (hasChanges: TeamState['team']['hasChanges']) => void;
	setIsTeamRequestLimitError: (teamRequestLimit: TeamState['team']['isTeamRequestLimitError']) => void;
	setSelectedTeamProperty: (teamProperty: TeamState['team']['teamProperty']) => void;
};

export type TeamSlice = TeamState & TeamActions;

const teamInitialState: TeamState = {
	team: {
		teamName: '',
		initialTeamName: '',
		teamId: '',
		teams: [],
		state: '',
		team: {
			properties: {
				ENABLE_MULTIUSE: 'true',
				ENABLE_SINGLEUSE: 'true',
				ENABLE_SUBSCRIPTIONS: 'true',
				REQUEST_LIMIT: '',
			},
		},
		features: {},
		properties: {},
		decoratedUser: {},
		teamRequestLimit: '',
		teamProperty: '',
		owners: [],
		hasChanges: false,
		isTeamNameError: false,
		isTeamRequestLimitError: false,
	},
};

export function teamSlice(set: NamedSet<ZustandStore>): TeamSlice {
	return {
		...teamInitialState,
		setTeam(team: TeamState['team']['team']): void {
			set(
				(state: TeamState) => {
					state.team.team = team;
					state.team.teamRequestLimit = team?.properties?.[teamFeaturesConstants.requestLimit] || '';
					return state;
				},
				false,
				'[setTeam]',
			);
		},
		setTeams(teams: TeamState['team']['teams']): void {
			set(
				(state: TeamState) => {
					state.team.teams = teams || [];
					return state;
				},
				false,
				'[setTeams]',
			);
		},
		setTeamOwners(owners: TeamState['team']['owners']): void {
			set(
				(state: TeamState) => {
					state.team.owners = owners;
					return state;
				},
				false,
				'[setTeamOwners]',
			);
		},
		setTeamId(teamId: TeamState['team']['teamId']): void {
			set(
				(state: TeamState) => {
					state.team.teamId = teamId;
					return state;
				},
				false,
				'[setTeamId]',
			);
		},
		setTeamName(teamName: TeamState['team']['teamName']): void {
			set(
				(state: TeamState) => {
					state.team.teamName = teamName;
					return state;
				},
				false,
				'[setTeamName]',
			);
		},
		setInitialTeamName(initialTeamName: TeamState['team']['initialTeamName']): void {
			set(
				(state: TeamState) => {
					state.team.initialTeamName = initialTeamName;
					return state;
				},
				false,
				'[setInitialTeamName]',
			);
		},
		setIsTeamNameError(isTeamNameError: TeamState['team']['isTeamNameError']): void {
			set(
				(state: TeamState) => {
					state.team.isTeamNameError = isTeamNameError;
					return state;
				},
				false,
				'[setIsTeamNameError]',
			);
		},
		setTeamProperty(properties: any): void {
			set(
				(state: any) => {
					state.team.teams =
						state.team.teams && state.team.teams.length > 0
							? state.team.teams.map(teamData => {
									if (_.isEqual(teamData.id, properties?.teamId)) {
										teamData.properties[properties?.propertyKey] = properties?.propertyValue;
									}
									return teamData;
								})
							: [];
					return state;
				},
				false,
				'[setTeams]',
			);
		},
		setSelectedTeamProperty(teamProperty: TeamState['team']['teamProperty']): void {
			set(
				(state: TeamState) => {
					state.team.teamProperty = teamProperty;
					return state;
				},
				false,
				'[setSelectedTeamProperty]',
			);
		},
		setTeamRequestLimit(teamRequestLimit: TeamState['team']['teamRequestLimit']): void {
			set(
				(state: TeamState) => {
					state.team.teamRequestLimit = teamRequestLimit;
					return state;
				},
				false,
				'[setTeamRequestLimit]',
			);
		},
		setIsTeamRequestLimitError(isTeamRequestLimitError: TeamState['team']['isTeamRequestLimitError']): void {
			set(
				(state: TeamState) => {
					state.team.isTeamRequestLimitError = isTeamRequestLimitError;
					return state;
				},
				false,
				'[setIsTeamRequestLimitError]',
			);
		},
		setHasChanges(hasChanges: TeamState['team']['hasChanges']): void {
			set(
				(state: TeamState) => {
					state.team.hasChanges = hasChanges;
					return state;
				},
				false,
				'[setHasChanges]',
			);
		},
		setDecoratedUser(decoratedUser: TeamState['team']['decoratedUser']): void {
			set(
				(state: TeamState) => {
					state.team.decoratedUser = decoratedUser;
					return state;
				},
				false,
				'[setDecoratedUser]',
			);
		},
	};
}
